import { EnhancedRouteItem } from "../../types";

export interface RouteValidationError {
  message: string;
  invalidStopIds: string[];
}

/**
 * Validates that for each cargo item, the pickup stop comes before the delivery stop.
 * @param stops Array of route stops to validate
 * @returns boolean indicating whether the route order is valid
 */
export const validateRouteOrder = (stops: EnhancedRouteItem[]): boolean => {
  return getRouteValidationError(stops) === null;
};

/**
 * Gets a detailed error message if the route order is invalid.
 * @param stops Array of route stops to validate
 * @returns RouteValidationError object if invalid, null if valid
 */
export const getRouteValidationError = (stops: EnhancedRouteItem[], availableStops: EnhancedRouteItem[] = []): RouteValidationError | null => {
  // Create a map to store cargo validation info
  const cargoValidation = new Map<string, {
    pickupStop?: EnhancedRouteItem;
    deliveryStops: EnhancedRouteItem[]; // Changed to array to handle multiple deliveries
    cargoId: string;
  }>();

  // First pass: collect all cargo pickup and delivery stops
  stops.forEach((stop, index) => {
    stop.cargos?.forEach(cargo => {
      if (!cargoValidation.has(cargo.id)) {
        cargoValidation.set(cargo.id, { cargoId: cargo.id, deliveryStops: [] });
      }

      const validation = cargoValidation.get(cargo.id)!;
      if (stop.isPickup) {
        validation.pickupStop = { ...stop, stopNumber: index + 1 };
      } else {
        // Add to array of delivery stops instead of overwriting
        validation.deliveryStops.push({ ...stop, stopNumber: index + 1 });
      }
    });
  });

  // Second pass: validate and collect errors
  const invalidStops: { stopId: string; message: string }[] = [];
  
  for (const [cargoId, validation] of Array.from(cargoValidation.entries())) {
    // Check for missing pickups when there are deliveries
    if (validation.deliveryStops.length > 0 && !validation.pickupStop) {
      // Check if the pickup exists in available stops
      const pickupExistsInAvailable = availableStops.some(stop => 
        stop.cargos?.some(cargo => cargo.id === cargoId && stop.isPickup)
      );

      // Show validation error if the pickup is in available stops
      // If pickup is not in available and not in route, we assume it's already been completed or is being picked up a different day
      if (pickupExistsInAvailable) {
        validation.deliveryStops.forEach(deliveryStop => {
          invalidStops.push({
            stopId: deliveryStop.location.id,
            message: `Pickup stop must be added to route before delivery for cargo ${cargoId}`
          });
        });
      }
    }
    
    // Check for invalid order (delivery before pickup) - now checking ALL delivery stops
    if (validation.pickupStop && validation.deliveryStops.length > 0) {
      validation.deliveryStops.forEach(deliveryStop => {
        if (deliveryStop.stopNumber <= validation.pickupStop!.stopNumber) {
          invalidStops.push({
            stopId: validation.pickupStop!.location.id,
            message: `Pickup stop #${validation.pickupStop!.stopNumber} must come before delivery stop #${deliveryStop.stopNumber} for cargo ${cargoId}`
          });
          invalidStops.push({
            stopId: deliveryStop.location.id,
            message: `Delivery stop #${deliveryStop.stopNumber} must come after pickup stop #${validation.pickupStop!.stopNumber} for cargo ${cargoId}`
          });
        }
      });
    }
  }

  if (invalidStops.length > 0) {
    const uniqueInvalidStopIds = Array.from(new Set(invalidStops.map(stop => stop.stopId)));
    const messages = Array.from(new Set(invalidStops.map(stop => stop.message)));

    return {
      message: messages.join('. '),
      invalidStopIds: uniqueInvalidStopIds
    };
  }

  return null;
}; 